/**
 * /segments/new — visual builder for a new segment. Saves to /segments,
 * navigates to the edit route on success.
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import {
  EMPTY_SEGMENT,
  type FieldDescriptor,
  type Segment,
} from "../../../lib/segments-dsl.js";
import { SegmentBuilder, useSegmentPreview } from "../../../components/segment-builder.js";

export const Route = createFileRoute("/_app/segments/new")({
  component: NewSegmentPage,
});

function NewSegmentPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [filter, setFilter] = useState<Segment>(EMPTY_SEGMENT);
  const [fields, setFields] = useState<FieldDescriptor[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = useSegmentPreview(filter);

  useEffect(() => {
    api
      .get<{ fields: FieldDescriptor[] }>("/segments/fields")
      .then((r) => setFields(r.data.fields))
      .catch(() => setError("Failed to load field registry"));
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ segment: { id: string } }>("/segments", {
        name: name.trim(),
        description: description.trim() || null,
        filter,
      });
      void navigate({ to: "/segments/$segmentId", params: { segmentId: r.data.segment.id } });
    } catch (err) {
      const data = (err as { response?: { data?: { message?: string; errors?: { path: string; message: string }[] } } })
        .response?.data;
      const detail = data?.errors?.[0] ? ` (${data.errors[0].path}: ${data.errors[0].message})` : "";
      setError((data?.message ?? "Save failed") + detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ maxWidth: 880, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 8px", fontSize: 28 }}>New segment</h1>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Build a filter over your contacts. The count updates as you type.
      </p>

      <form onSubmit={save} style={{ display: "grid", gap: 14 }}>
        <div className="brut-card" style={{ padding: 14, display: "grid", gap: 10 }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Segment name (e.g. Recently active VIPs)"
            className="brut-input"
            maxLength={120}
            required
          />
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            className="brut-input"
            maxLength={1024}
          />
        </div>

        <SegmentBuilder value={filter} fields={fields} onChange={setFilter} />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
          }}
        >
          <PreviewBadge {...preview} />
          {error ? (
            <div style={{ color: "#b40000", fontSize: 13, flex: 1 }}>{error}</div>
          ) : null}
          <button
            type="submit"
            disabled={!name.trim() || busy}
            className="brut-button"
          >
            {busy ? "Saving…" : "Save segment"}
          </button>
        </div>
      </form>
    </section>
  );
}

interface PreviewBadgeProps {
  count: number | null;
  capped: boolean;
  loading: boolean;
  error: string | null;
}

export function PreviewBadge({ count, capped, loading, error }: PreviewBadgeProps) {
  if (error) {
    return (
      <span style={{ fontSize: 13, color: "#b40000" }} title={error}>
        Preview error
      </span>
    );
  }
  if (loading || count === null) {
    return <span style={{ fontSize: 13, color: "var(--muted)" }}>Counting…</span>;
  }
  return (
    <span
      className="brut-card"
      style={{ padding: "6px 12px", fontWeight: 800, fontSize: 14 }}
    >
      Matches {count.toLocaleString()}
      {capped ? "+" : ""} {count === 1 ? "contact" : "contacts"}
    </span>
  );
}
